import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronLeft,
  Download,
  FileText,
  Image as ImageIcon,
  ListChecks,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PhotoGridItem } from "@/components/records/photo-grid-item/photo-grid-item";
import type { StatusSelection } from "@/components/issues/issue-status-nav/issue-status-nav";
import { ReportsOfflineState } from "@/components/reports/reports-offline-state/reports-offline-state";
import {
  CollectionAddSheet,
  type ReportMembership,
} from "@/components/reports/collection-add-sheet/collection-add-sheet";
import { useCanOnResource, usePermissions } from "@/hooks/use-permissions";
import { useOnlineStatus } from "@/offline/online-status";
import { cn } from "@/lib/utils";
import { CollectionExportsSection } from "./collection-exports-section";
import { ItemCell, Tombstone } from "./collection-item-cell";
import { ExportFormatPicker } from "./export-format-picker";
import { buildExportInput, resolveExportFormat } from "./export-options";
import { ReportIssueList } from "./report-issue-list";
import { useCollection } from "./use-collection";
import { useCollectionItems } from "./use-collection-items";
import {
  useAddCollectionItems,
  useCreateCollectionExport,
  useDeleteCollection,
  useRemoveCollectionItem,
  useRenameCollection,
} from "./use-collection-mutations";
import type {
  ProjectDetail,
  ProjectIssue,
  ProjectRecord,
  ReportFormat,
} from "@/types/api";

/** How far below the scroller's bottom edge the next page is asked for. */
const END_LEAD_PX = 600;

/** Whether the paging sentinel sits within the lead of the scroller's bottom. */
function endIsNear(sentinel: HTMLElement | null, scroller: HTMLElement | null) {
  if (!sentinel || !scroller) return false;
  return (
    sentinel.getBoundingClientRect().top <=
    scroller.getBoundingClientRect().bottom + END_LEAD_PX
  );
}

interface CollectionDetailViewProps {
  projectId: string;
  collectionId: string;
  project: ProjectDetail;
  /**
   * Open an issue row inside the report rather than following its link to the
   * issue page. See [[report-detail-page.spec]] § Opening an issue.
   */
  onOpenIssue?: (issue: ProjectIssue) => void;
  /** The issue open in the side panel, highlighted in the list. */
  activeIssueId?: string | null;
  /**
   * The side panel is open beside the report, so an issues report draws cards
   * even on a wide window, as the Issues page does.
   */
  forceIssueCards?: boolean;
  /** The selected status tab of an issues report; null is All. */
  statusFilter?: StatusSelection;
  onStatusFilterChange?: (status: StatusSelection) => void;
  /**
   * Open a photo's record inside the report, as the Photos page opens it. See
   * [[report-detail-page.spec]] § Opening a photo.
   */
  onOpenRecord?: (record: ProjectRecord) => void;
  /** The record open in the side panel, highlighted in the grid. */
  activeRecordId?: string | null;
}

/**
 * One report: the list it holds, and the files it has produced.
 *
 * The contents are drawn by the components that already draw them elsewhere —
 * the photo tile and the issue row — so a report is not a third way of looking
 * at a photo. What this adds around them is a header, an Add action, removal,
 * tombstones and the exports section. See [[report-detail-page.spec]].
 */
export function CollectionDetailView({
  projectId,
  collectionId,
  project,
  onOpenIssue,
  activeIssueId = null,
  forceIssueCards = false,
  statusFilter = null,
  onStatusFilterChange,
  onOpenRecord,
  activeRecordId = null,
}: CollectionDetailViewProps) {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { can } = usePermissions({
    owner: project.owner.id,
    my_roles: project.my_roles,
  });
  const canOnResource = useCanOnResource({
    owner: project.owner.id,
    my_roles: project.my_roles,
  });

  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  // Null means "whatever the collection used last". Held as an override rather
  // than seeded into state because the collection arrives a render after this
  // component mounts — seeding would latch a placeholder and never correct it.
  const [formatOverride, setFormatOverride] = useState<ReportFormat | null>(
    null,
  );

  const { data: collection, isLoading } = useCollection(
    projectId,
    collectionId,
  );
  const {
    data: itemData,
    isLoading: itemsLoading,
    isFetching: itemsFetching,
    isFetchingNextPage,
    isLoadingError: itemsLoadError,
    isFetchNextPageError: nextPageError,
    hasNextPage,
    fetchNextPage,
    refetch: refetchItems,
  } = useCollectionItems(projectId, collectionId);
  const addItems = useAddCollectionItems(projectId, collectionId);
  const removeItem = useRemoveCollectionItem(projectId, collectionId);
  const createExport = useCreateCollectionExport(projectId, collectionId);
  const renameCollection = useRenameCollection(projectId);
  const deleteCollection = useDeleteCollection(projectId);

  const items = itemData?.items ?? [];
  const existingIds = useMemo(
    () =>
      new Set(
        items
          .map((item) => item.record?.id ?? item.issue?.id)
          .filter((id): id is string => Boolean(id)),
      ),
    [items],
  );

  // The contents load a page at a time. The sentinel under them reports when
  // the end nears the bottom of this view's own scroller — observed against
  // the scroller rather than the viewport, because the viewport would clip it
  // to the scroller's edge and the margin would never lead.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [endInView, setEndInView] = useState(false);
  useEffect(() => {
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => setEndInView(entries.some((entry) => entry.isIntersecting)),
      { root: scrollRef.current, rootMargin: `0px 0px ${END_LEAD_PX}px 0px` },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      setEndInView(false);
    };
  }, [sentinel]);

  // More of the report is wanted while its end is in view, and — wherever
  // the user has scrolled — while Add is open: the picker can only hide what
  // the report holds once all of it is loaded. One effect serves both so they
  // never race for a page. It waits out any fetch in flight, and a failed
  // page stops it rather than re-firing in a loop; Try again resumes it. See
  // [[report-detail-page.spec]] § Contents load as you scroll.
  // A status tab filters what is loaded, so choosing one wants the whole report
  // for the same reason: a tab over part of it would leave rows out. See
  // [[report-detail-page.spec]] § Status tabs.
  const filteringIssues = collection?.kind === "issues" && statusFilter !== null;
  const wantMore = endInView || addOpen || filteringIssues;
  // Re-runs on every page that lands, not just on `isFetching` flipping: a
  // fast response can start and finish inside one batched update, leaving no
  // render in which the fetch was visible — and the end may still be in view.
  // But `endInView` is the observer's last report, and that lags a page that
  // just landed: its rows are on screen, the sentinel has moved a page further
  // down, and the observer has yet to say so. Trusting it asks for the page
  // after as well, two pages for one scroll. So when only the scroll position
  // wants more, measure the sentinel again before asking.
  const loadedCount = items.length;
  useEffect(() => {
    if (!wantMore || !hasNextPage || itemsFetching || nextPageError) return;
    if (!addOpen && !filteringIssues && !endIsNear(sentinel, scrollRef.current)) {
      return;
    }
    void fetchNextPage();
  }, [
    wantMore,
    hasNextPage,
    itemsFetching,
    nextPageError,
    fetchNextPage,
    loadedCount,
    addOpen,
    filteringIssues,
    sentinel,
  ]);

  const membership: ReportMembership =
    itemData && !hasNextPage
      ? { status: "ready", ids: existingIds }
      : !itemsFetching && (itemsLoadError || nextPageError)
        ? {
            status: "error",
            retry: () => void (itemData ? fetchNextPage() : refetchItems()),
          }
        : { status: "loading" };

  // On a phone this page has no tab bar ([[bottom-nav.spec]] § Nested
  // screens), so the back arrow is the way out of every state, not just the
  // loaded one.
  if (!isOnline) {
    return (
      <div className="flex h-full flex-1 flex-col">
        <div className="mx-auto w-full max-w-4xl px-4 pt-6">
          <BackToReports projectId={projectId} />
        </div>
        <ReportsOfflineState />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-3 px-4 py-6">
        <div className="flex items-start gap-2">
          <BackToReports projectId={projectId} />
          <Skeleton className="h-16 min-w-0 flex-1" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // Deliberately `!collection` and not `isError` — TanStack keeps the cached
  // collection when a *background* refetch throws, and this page polls every 3s
  // while an export runs. Branching on `isError` would swap the whole page for
  // "Something went wrong" on one dropped poll, taking the progress bar and an
  // open exports panel with it, then swap back 3s later. A failed first load
  // has no cached collection and still lands here.
  if (!collection) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-4 px-4 py-16 text-center">
        <h3 className="text-lg font-semibold">Something went wrong</h3>
        <p className="text-sm text-muted-foreground">
          This report could not be loaded.
        </p>
        <Button
          variant="outline"
          onClick={() =>
            navigate({
              to: "/projects/$projectId/reports",
              params: { projectId },
            })
          }
        >
          Back to reports
        </Button>
      </div>
    );
  }

  const isPhotos = collection.kind === "records";
  const owner = collection.created_by?.id ?? "";
  const canEdit = canOnResource("report", "Update", owner);
  const canDelete = canOnResource("report", "Delete", owner);
  const canExport = can("report:Create");
  const latest = collection.latest_export;
  const exporting =
    latest?.status === "PENDING" || latest?.status === "PROCESSING";
  const liveCount = isPhotos
    ? (collection.photo_count ?? 0)
    : collection.item_count;
  const selectedFormat = resolveExportFormat(collection, formatOverride);
  const exportDisabled =
    exporting || createExport.isPending || liveCount === 0;

  return (
    <div ref={scrollRef} className="flex h-full flex-1 flex-col overflow-auto">
      {/* Photos keep the reports list's centred column. An issues report takes
          the full width, as the Issues page does, so its table has the room
          the Issues page's table has. */}
      <div
        className={cn(
          "flex w-full flex-col gap-4 px-4 py-6",
          isPhotos && "mx-auto max-w-4xl",
        )}
      >
        <header className="flex flex-col gap-3">
          <div className="flex items-start gap-2">
            <BackToReports projectId={projectId} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <h1 className="truncate text-xl font-semibold">
                  {collection.name}
                </h1>
                <Badge variant="secondary" className="shrink-0 gap-1">
                  {/* The kind's colour, as on the report's row in the list. */}
                  {isPhotos ? (
                    <ImageIcon className="size-3 text-kind-photos" />
                  ) : (
                    <ListChecks className="size-3 text-kind-issues" />
                  )}
                  {isPhotos ? "Photos" : "Issues"}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {liveCount} {isPhotos ? "photos" : "issues"}
              </p>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More actions">
                  <MoreVertical className="size-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem
                    onClick={() => {
                      setDraftName(collection.name);
                      setRenameOpen(true);
                    }}
                  >
                    <Pencil className="mr-2 size-4" />
                    Rename
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 className="mr-2 size-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-wrap gap-2">
            {canExport && (
              <>
                <Button
                  onClick={() =>
                    createExport.mutate(
                      buildExportInput(collection, formatOverride),
                    )
                  }
                  disabled={exportDisabled}
                >
                  <Download className="mr-1 size-4" />
                  Export
                </Button>
                {/* No "Exporting…" label: the exports row below the header
                    carries the whole lifecycle — the bar, then the file with
                    Open, or the reason with Retry. */}
                <ExportFormatPicker
                  value={selectedFormat}
                  onChange={setFormatOverride}
                  disabled={exportDisabled}
                />
              </>
            )}
            {canEdit && (
              <>
                <Button variant="outline" onClick={() => setAddOpen(true)}>
                  <Plus className="mr-1 size-4" />
                  Add
                </Button>
                <Button
                  variant={editing ? "secondary" : "outline"}
                  onClick={() => setEditing((prev) => !prev)}
                >
                  {editing ? "Done" : "Edit"}
                </Button>
              </>
            )}
          </div>

          {collection.deleted_item_count > 0 && (
            <p className="text-sm text-muted-foreground">
              {collection.deleted_item_count} item
              {collection.deleted_item_count === 1 ? " has" : "s have"} been
              deleted from the project and will not appear in the file.
            </p>
          )}
        </header>

        {/* Above the contents, never below: a 200-photo report puts anything
            under the grid out of reach. */}
        <CollectionExportsSection
          projectId={projectId}
          collectionId={collectionId}
          kind={collection.kind}
          latestExport={latest}
          onRetry={
            canExport
              ? (format) =>
                  createExport.mutate(buildExportInput(collection, format))
              : undefined
          }
          retrying={createExport.isPending}
        />

        {itemsLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <FileText className="size-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              This report is empty. Add {isPhotos ? "photos" : "issues"} to it.
            </p>
            {canEdit && (
              <Button variant="outline" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1 size-4" />
                Add {isPhotos ? "photos" : "issues"}
              </Button>
            )}
          </div>
        ) : isPhotos ? (
          <div className="grid grid-cols-3 gap-1 sm:grid-cols-4 md:grid-cols-5">
            {items.map((item) => (
              <ItemCell
                key={item.id}
                item={item}
                editing={editing}
                onRemove={() => removeItem.mutate(item.id)}
              >
                {/* A tombstone has nothing to open, and no thumbnail. */}
                {item.record && !item.is_deleted ? (
                  <PhotoGridItem
                    record={item.record}
                    onClick={onOpenRecord}
                    isActive={item.record.id === activeRecordId}
                  />
                ) : (
                  <Tombstone />
                )}
              </ItemCell>
            ))}
          </div>
        ) : (
          <ReportIssueList
            projectId={projectId}
            items={items}
            complete={!!itemData && !hasNextPage}
            editing={editing}
            onRemove={(itemId) => removeItem.mutate(itemId)}
            status={statusFilter}
            onStatusChange={(status) => onStatusFilterChange?.(status)}
            onOpenIssue={onOpenIssue}
            activeIssueId={activeIssueId}
            forceCards={forceIssueCards}
          />
        )}

        {items.length > 0 && (
          <>
            {isFetchingNextPage && <Skeleton className="h-16 w-full" />}
            {nextPageError && !itemsFetching && (
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Couldn&apos;t load the rest of this report.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void fetchNextPage()}
                >
                  Try again
                </Button>
              </div>
            )}
            <div ref={setSentinel} aria-hidden="true" />
          </>
        )}
      </div>

      <CollectionAddSheet
        projectId={projectId}
        kind={collection.kind}
        membership={membership}
        open={addOpen}
        onOpenChange={setAddOpen}
        isAdding={addItems.isPending}
        onAdd={(ids) =>
          addItems.mutate(ids, { onSuccess: () => setAddOpen(false) })
        }
      />

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename report</DialogTitle>
            <DialogDescription>
              Files you have already exported keep the name they were sent
              under.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={draftName}
            onChange={(event) => setDraftName(event.target.value)}
            aria-label="Report name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!draftName.trim() || renameCollection.isPending}
              onClick={() =>
                renameCollection.mutate(
                  { id: collectionId, name: draftName.trim() },
                  { onSuccess: () => setRenameOpen(false) },
                )
              }
            >
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this report?</DialogTitle>
            <DialogDescription>
              Every version you have exported from it is deleted too. The photos
              and issues themselves stay in the project.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteCollection.isPending}
              onClick={() =>
                deleteCollection.mutate(collectionId, {
                  onSuccess: () =>
                    navigate({
                      to: "/projects/$projectId/reports",
                      params: { projectId },
                    }),
                })
              }
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Leads the title row: this page is one level below the Reports tab. A link,
 * not a step back through history, so it still goes somewhere sensible after a
 * refresh or from a shared link. See [[report-detail-page.spec]] US-010.
 */
function BackToReports({ projectId }: { projectId: string }) {
  return (
    <Link
      to="/projects/$projectId/reports"
      params={{ projectId }}
      aria-label="Back to reports"
      // Pulled up and left so the arrow centres on the title's line and sits
      // on the column's edge, while keeping the issue page's 36px target.
      className="-ml-2 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center"
    >
      <ChevronLeft className="h-5 w-5" />
    </Link>
  );
}
